import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { uniqueId } from 'lodash'
import Modal from '../../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import NullableDateCalendarModule from '../NullableDateCalendarModule'
import { dateManipulateDay } from '../../../../utils/date'

interface MemberCost {
  period_start: string | null
  period_end: string | null
  hourly_pay: number
}

interface ModalState {
  id: number
  name: string
  member_costs: MemberCost[]
}

interface EditingMemberCost {
  uid: string
  period_start: string | null
  period_end: string | null
  hourly_pay: string
}

interface Props {
  modalState: ModalState
  closeCallback: VoidFunction
  modCallback: (id: number, memberCosts: MemberCost[]) => void
  isDisplay: boolean
}

const toEditing = (mc: MemberCost): EditingMemberCost => ({
  uid: uniqueId(),
  period_start: mc.period_start,
  period_end: mc.period_end,
  hourly_pay: String(mc.hourly_pay),
})

const normalizeHourlyPay = (value: string): string => {
  const normalized = value
    .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    .replace(/[^0-9]/g, '')
  return normalized === '' ? '0' : normalized
}

const emptyRow = (): EditingMemberCost => ({ uid: uniqueId(), period_start: null, period_end: null, hourly_pay: '' })

interface RowProps {
  row: EditingMemberCost
  onChangeField: (uid: string, field: keyof EditingMemberCost, value: string | null) => void
  onBlurPay: (uid: string) => void
  onDelete: (uid: string) => void
}

const CostRow: FC<RowProps> = React.memo((props) => {
  const {
    row, onChangeField, onBlurPay, onDelete,
  } = props

  const handleStart = useCallback(
    (updated_date: string | null) => onChangeField(row.uid, 'period_start', updated_date),
    [row.uid, onChangeField],
  )
  const handleEnd = useCallback(
    (updated_date: string | null) => onChangeField(row.uid, 'period_end', updated_date),
    [row.uid, onChangeField],
  )

  return (
    <div className="cost_grid_row">
      <div>
        <NullableDateCalendarModule
          initialDate={row.period_start}
          isNotSet={row.period_start === null}
          disabled={false}
          future
          wrapperClass="cost_modal_calendar"
          changeCallback={handleStart}
          popupDisplayType="bottom"
        />
      </div>
      <div aria-hidden="true">〜</div>
      <div>
        <NullableDateCalendarModule
          initialDate={row.period_end}
          isNotSet={row.period_end === null}
          disabled={false}
          future
          wrapperClass="cost_modal_calendar"
          changeCallback={handleEnd}
          popupDisplayType="bottom"
        />
      </div>
      <div aria-hidden="true" />
      <div className="cost_grid_cell_input">
        <input
          type="text"
          value={row.hourly_pay}
          onChange={(e) => onChangeField(row.uid, 'hourly_pay', e.target.value)}
          onBlur={() => onBlurPay(row.uid)}
          placeholder="未設定"
        />
      </div>
      <div>
        <button
          type="button"
          onClick={() => onDelete(row.uid)}
          style={{ color: "#FF0000", fontSize: "20px" }}
        >
          <FontAwesomeIcon icon={['fas', 'trash-alt']} />
        </button>
      </div>
    </div>
  )
})

const CostModal: FC<Props> = (props) => {
  const {
    modalState,
    closeCallback,
    modCallback,
    isDisplay,
  } = props

  const [editingCosts, setEditingCosts] = useState<EditingMemberCost[]>([])

  const validationErrors = useMemo(() => {
    const errors: { id: string; message: string }[] = []
    const reversedIndexes = new Set<number>()
    editingCosts.forEach((editingCost, i) => {
      if (
        editingCost.period_start
        && editingCost.period_end
        && new Date(editingCost.period_start) > new Date(editingCost.period_end)
      ) {
        reversedIndexes.add(i)
        errors.push({ id: `reversed-${i}`, message: `${i + 1}番目の開始日が終了日より後になっています。` })
      }
    })
    editingCosts.forEach((left, i) => {
      if (reversedIndexes.has(i)) return
      editingCosts.slice(i + 1).forEach((right, offset) => {
        const j = i + 1 + offset
        if (reversedIndexes.has(j)) return
        const leftStart = left.period_start ? new Date(left.period_start).getTime() : -Infinity
        const leftEnd = left.period_end ? new Date(left.period_end).getTime() : Infinity
        const rightStart = right.period_start ? new Date(right.period_start).getTime() : -Infinity
        const rightEnd = right.period_end ? new Date(right.period_end).getTime() : Infinity
        if (leftStart <= rightEnd && rightStart <= leftEnd) {
          errors.push({ id: `overlap-${i}-${j}`, message: `${i + 1}番目と${j + 1}番目の期間が重複しています。` })
        }
      })
    })
    return errors
  }, [editingCosts])

  const isUnchanged = useMemo(() => {
    if (editingCosts.length !== modalState.member_costs.length) return false
    return editingCosts.every((editingCost, i) => {
      const beforeEditCost = modalState.member_costs[i]
      return (
        editingCost.period_start === beforeEditCost.period_start
        && editingCost.period_end === beforeEditCost.period_end
        && (Number(editingCost.hourly_pay) || 0) === beforeEditCost.hourly_pay
      )
    })
  }, [editingCosts, modalState.member_costs])

  const onChangeField = useCallback((uid: string, field: keyof EditingMemberCost, value: string | null) => {
    setEditingCosts((prev) => prev.map((row) => (row.uid === uid ? { ...row, [field]: value } : row)))
  }, [])

  const onBlurPay = useCallback((uid: string) => {
    setEditingCosts((prev) => prev.map((row) => (
      row.uid === uid ? { ...row, hourly_pay: normalizeHourlyPay(row.hourly_pay) } : row
    )))
  }, [])

  const addRow = () => {
    setEditingCosts((prev) => {
      if (prev.length === 0) return [emptyRow()]
      const lastEnd = prev[prev.length - 1].period_end
      const nextStart = lastEnd === null ? null : dateManipulateDay(lastEnd, 1, 'YYYY-MM-DD')
      return [...prev, { ...emptyRow(), period_start: nextStart }]
    })
  }

  const onDelete = useCallback((uid: string) => {
    setEditingCosts((prev) => prev.filter((row) => row.uid !== uid))
  }, [])

  const submitCost = () => {
    const memberCosts: MemberCost[] = editingCosts.map((row) => ({
      period_start: row.period_start,
      period_end: row.period_end,
      hourly_pay: Number(normalizeHourlyPay(row.hourly_pay)) || 0,
    }))
    modCallback(modalState.id, memberCosts)
  }

  useEffect(() => {
    if (isDisplay) {
      setEditingCosts(
        modalState.member_costs.length > 0
          ? modalState.member_costs.map(toEditing)
          : [emptyRow()],
      )
    }
  }, [isDisplay])

  return (
    <Modal
      title="コスト編集"
      show={isDisplay}
      wrapperClass="cost_modal"
      closeCallback={closeCallback}
      closeable
    >
      <>
        <div
          style={{
            marginBottom: "24px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {`${modalState.name}さんの1時間あたりのコスト`}
        </div>
        <div className="cost_grid">
          <div className="cost_grid_row cost_grid_header">
            <div>適用開始日</div>
            <div aria-hidden="true" />
            <div>適用終了日</div>
            <div aria-hidden="true" />
            <div className="cost_grid_cell_input">
              1時間あたりの
              <br />
              コスト(円)
            </div>
            <div aria-label="操作" />
          </div>
          {editingCosts.map((row) => (
            <CostRow
              key={row.uid}
              row={row}
              onChangeField={onChangeField}
              onBlurPay={onBlurPay}
              onDelete={onDelete}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => addRow()}
          style={{
            display: "inline-block",
            width: "100%",
            backgroundColor: "#d6e8d4",
            border: "1px solid #81b365",
            color: "#81b365",
            padding: "12px",
            borderRadius: "4px",
            cursor: "pointer",
            textAlign: "center",
          }}
        >
          <FontAwesomeIcon icon={['fas', 'plus']} />
          &nbsp;
          追加
        </button>
        {validationErrors.length > 0 && (
          <div style={{ color: "#FF0000", fontSize: "14px", marginTop: "8px" }}>
            {validationErrors.map((error) => (
              <div key={error.id}>{error.message}</div>
            ))}
          </div>
        )}
      </>
      <>
        <button
          type="button"
          className="button cancel"
          onClick={closeCallback}
        >
          キャンセル
        </button>
        <button
          type="button"
          className="button"
          onClick={submitCost}
          disabled={isUnchanged || validationErrors.length > 0}
        >
          編集する
        </button>
      </>
    </Modal>
  )
}

export default CostModal
